"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { AiFillStar } from "react-icons/ai";
import { getRoomsData } from "@/data/rooms";
import { useLanguage, useTranslations } from "@/lib/i18n";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
    },
  },
};

export default function Rooms() {
  const { language } = useLanguage();
  const t = useTranslations();
  const roomsData = getRoomsData(language);

  return (
    <section id="rooms" className="py-24 md:py-32 bg-brand-cream">
      <div className="container mx-auto px-6 md:px-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-6">
            <AiFillStar size={14} className="text-brand-gold" />
            <span className="text-brand-gold text-xs font-bold tracking-widest uppercase">
              {t.rooms.flexiStay}
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl text-brand-charcoal mb-6">
            {t.rooms.title}
          </h2>
          <p className="text-gray-500 font-light max-w-xl mx-auto text-lg">
            {t.rooms.subtitle}
          </p>
        </motion.div>

        {/* Grid Kamar */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {roomsData.map((room) => (
            <motion.div
              key={room.id}
              variants={cardVariants}
              className="group relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col"
            >
              {/* Badge Best Value */}
              {room.isBestValue && (
                <div className="absolute top-4 right-4 z-30 bg-brand-gold text-white text-[10px] font-bold px-3 py-1 uppercase tracking-wider shadow-lg">
                  {t.rooms.mostPopular}
                </div>
              )}

              {/* Gambar */}
              <div className="relative h-[280px] overflow-hidden">
                <Image
                  src={room.images.main}
                  alt={room.name}
                  fill
                  className="object-cover transition-transform duration-[1.5s] ease-in-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />

                {/* Overlay Button Hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 bg-black/20 backdrop-blur-[2px]">
                  <Link href="/rooms">
                    <Button className="bg-white text-black hover:bg-brand-gold hover:text-white border-none px-8 py-6 uppercase tracking-widest text-xs font-bold transition-all transform translate-y-4 group-hover:translate-y-0">
                      {t.rooms.viewDetails}
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="font-serif text-2xl text-brand-charcoal mb-2 group-hover:text-brand-gold transition-colors">
                  {room.name}
                </h3>
                {/* Deskripsi Pendek */}
                <p className="text-gray-500 text-sm font-light mb-6 line-clamp-2 flex-grow">
                  {room.description.short}
                </p>

                {/* Features List Mini */}
                <div className="flex gap-3 mb-6 flex-wrap">
                  {room.features.summary.slice(0, 2).map((feat, i) => (
                    <span
                      key={i}
                      className="text-[10px] uppercase font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-sm border border-gray-100"
                    >
                      {feat}
                    </span>
                  ))}
                  <span className="text-[10px] uppercase font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-sm border border-gray-100">
                    {t.rooms.more}
                  </span>
                </div>

                {/* PRICING SECTION */}
                <div className="mt-auto border-t border-dashed border-gray-200 pt-5 space-y-3">
                  {/* Harga Harian */}
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                      {t.rooms.daily}
                    </span>
                    <div className="text-right">
                      <span className="font-serif text-lg text-brand-charcoal font-bold block leading-none">
                        IDR {room.prices.daily.value}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        / {room.prices.daily.unit}
                      </span>
                    </div>
                  </div>

                  {/* Harga Bulanan */}
                  <div className="flex justify-between items-center bg-[#FAFAFA] -mx-6 px-6 py-2 border-l-4 border-brand-gold">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-brand-gold uppercase tracking-wider">
                        {t.rooms.bestValue}
                      </span>
                      <span className="text-xs text-gray-400">{t.rooms.monthly}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-serif text-xl text-brand-gold font-bold block leading-none">
                        IDR {room.prices.monthly.value}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link href="/rooms">
            <Button
              variant="outline"
              className="rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white px-8 py-6 uppercase text-xs font-bold tracking-[0.2em] transition-all"
            >
              {t.rooms.exploreAll}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
